import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';

import {
  ARCHIVED_AT_FIELD,
  ARCHIVED_BY_AUTHOR_FIELD,
  CLIENT_OPPORTUNITY_LISTING_TYPES,
  LISTING_GROUP_ARCHIVED,
  LISTING_STATUS_MANUALLY_ARCHIVED,
  classifyClientOpportunityListing,
  isClientOpportunityCurrent,
  isClientOpportunityPast,
  isClientOpportunityListing,
} from './ProfilePage.listingClassification';

const { UUID } = sdkTypes;

const createArchiveDraftError = message => {
  const error = new Error(message);
  error.status = 409;
  return error;
};

// ================ Async Thunks ================ //

///////////////
// Show User //
///////////////
const showUserPayloadCreator = ({ userId, config }, { dispatch, rejectWithValue, extra: sdk }) => {
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showUserThunk = createAsyncThunk('ProfilePage/showUser', showUserPayloadCreator);

// Backward compatible wrapper for the thunk
export const showUser = (userId, config) => dispatch => {
  return dispatch(showUserThunk({ userId, config }));
};

/////////////////////////
// Query User Listings //
/////////////////////////
const PROFILE_LISTINGS_PAGE_SIZE = 100;
const PROFILE_TRANSACTIONS_PAGE_SIZE = 100;

const uniqueResources = resources => {
  const resourcesById = new Map();
  resources.forEach(resource => {
    resourcesById.set(`${resource.type}:${resource.id.uuid}`, resource);
  });
  return [...resourcesById.values()];
};

const queryAllOwnListings = async (sdk, queryParams) => {
  const data = [];
  const included = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await sdk.ownListings.query({
      ...queryParams,
      page,
      perPage: PROFILE_LISTINGS_PAGE_SIZE,
    });
    data.push(...(response?.data?.data || []));
    included.push(...(response?.data?.included || []));
    totalPages = response?.data?.meta?.totalPages || 1;
    page += 1;
  } while (page <= totalPages);

  return {
    data: {
      data,
      included: uniqueResources(included),
      meta: { page: 1, totalPages: 1, totalItems: data.length, perPage: data.length },
    },
  };
};

export const queryAllOrderTransactionsForListing = async (sdk, listingId) => {
  const transactions = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await sdk.transactions.query({
      only: 'order',
      listingId,
      processNames: ['default-negotiation'],
      page,
      perPage: PROFILE_TRANSACTIONS_PAGE_SIZE,
      'fields.transaction': ['processName', 'processVersion', 'lastTransition'],
    });
    transactions.push(...(response?.data?.data || []));
    totalPages = response?.data?.meta?.totalPages || 1;
    page += 1;
  } while (page <= totalPages);

  return transactions;
};

const queryUserListingsPayloadCreator = async (
  { userId, config, ownProfileOnly = false },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const queryParams = {
    include: ['author', 'images'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  try {
    if (!ownProfileOnly) {
      const response = await sdk.listings.query({
        author_id: userId,
        ...queryParams,
      });
      // Pick only the id and type properties from the response listings
      const listings = response.data.data;
      const listingRefs = listings
        .filter(
          listing =>
            !listing.attributes.deleted &&
            listing.attributes.state === 'published' &&
            (!isClientOpportunityListing(listing) ||
              isClientOpportunityCurrent(listing) ||
              isClientOpportunityPast(listing))
        )
        .map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      return { listingRefs, ownClientListingRefs: [], listingClassifications: {}, response };
    }

    const response = await queryAllOwnListings(sdk, {
      ...queryParams,
      states: ['draft', 'pendingApproval', 'published', 'closed'],
    });
    const listings = response.data.data.filter(listing => !listing.attributes.deleted);
    const clientListings = listings.filter(listing =>
      CLIENT_OPPORTUNITY_LISTING_TYPES.includes(listing.attributes.publicData?.listingType)
    );
    const publicProviderListings = listings.filter(
      listing =>
        listing.attributes.state === 'published' &&
        !CLIENT_OPPORTUNITY_LISTING_TYPES.includes(listing.attributes.publicData?.listingType)
    );
    const transactionEntries = await Promise.all(
      clientListings.map(async listing => [
        listing.id.uuid,
        await queryAllOrderTransactionsForListing(sdk, listing.id),
      ])
    );
    const transactionsByListingId = Object.fromEntries(transactionEntries);
    const listingClassifications = Object.fromEntries(
      clientListings.map(listing => {
        const classification = classifyClientOpportunityListing({
          listing,
          transactions: transactionsByListingId[listing.id.uuid],
        });
        if (process.env.NODE_ENV === 'development' && classification?.unknownStates?.length > 0) {
          console.warn('[ProfilePage] Unknown client listing or transaction state.', {
            listingId: listing.id.uuid,
            states: classification.unknownStates,
          });
        }
        return [listing.id.uuid, classification];
      })
    );

    dispatch(addMarketplaceEntities(response));
    return {
      listingRefs: publicProviderListings.map(({ id, type }) => ({ id, type })),
      ownClientListingRefs: clientListings.map(({ id, type }) => ({ id, type })),
      listingClassifications,
      response,
    };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const queryUserListingsThunk = createAsyncThunk(
  'ProfilePage/queryUserListings',
  queryUserListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const queryUserListings = (userId, config, ownProfileOnly = false) => dispatch => {
  return dispatch(queryUserListingsThunk({ userId, config, ownProfileOnly }));
};

///////////////////////////
// Archive an Own Draft  //
///////////////////////////
const archiveOwnDraftPayloadCreator = async (
  { listingId, ownerId },
  { dispatch, getState, rejectWithValue, extra: sdk }
) => {
  try {
    const currentUserId = getState()?.user?.currentUser?.id;
    const profileUserId = getState()?.ProfilePage?.userId;
    const isOwnProfile =
      currentUserId?.uuid === ownerId?.uuid && profileUserId?.uuid === ownerId?.uuid;

    if (!isOwnProfile) {
      throw createArchiveDraftError('The listing does not belong to the current profile owner.');
    }

    const showResponse = await sdk.ownListings.show({ id: listingId });
    const listing = showResponse?.data?.data;
    const privateData = listing?.attributes?.privateData || {};

    if (!['draft', 'pendingApproval'].includes(listing?.attributes?.state)) {
      throw createArchiveDraftError(
        'Only a draft or pending approval listing can be archived from this page.'
      );
    }
    if (privateData[ARCHIVED_BY_AUTHOR_FIELD] === true) {
      throw createArchiveDraftError('This draft listing is already archived.');
    }

    const archivedAt = new Date().toISOString();
    const response = await sdk.ownListings.update(
      {
        id: listingId,
        privateData: {
          ...privateData,
          [ARCHIVED_BY_AUTHOR_FIELD]: true,
          [ARCHIVED_AT_FIELD]: archivedAt,
        },
      },
      { expand: true }
    );

    dispatch(addMarketplaceEntities(response));
    return { listingId: listingId.uuid, archivedAt };
  } catch (error) {
    return rejectWithValue(storableError(error));
  }
};

export const archiveOwnDraftThunk = createAsyncThunk(
  'ProfilePage/archiveOwnDraft',
  archiveOwnDraftPayloadCreator,
  {
    condition: (_args, { getState }) => !getState()?.ProfilePage?.archiveDraftInProgressId,
  }
);

export const archiveOwnDraft = (listingId, ownerId) => dispatch =>
  dispatch(archiveOwnDraftThunk({ listingId, ownerId })).unwrap();

//////////////////////////
// Query User's Reviews //
//////////////////////////
const queryUserReviewsPayloadCreator = ({ userId }, { rejectWithValue, extra: sdk }) => {
  return sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      return reviews;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryUserReviewsThunk = createAsyncThunk(
  'ProfilePage/queryUserReviews',
  queryUserReviewsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const queryUserReviews = userId => dispatch => {
  return dispatch(queryUserReviewsThunk({ userId }));
};

// ================ Slice ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  ownClientListingRefs: [],
  listingClassifications: {},
  archiveDraftInProgressId: null,
  archiveDraftError: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

const profilePageSlice = createSlice({
  name: 'ProfilePage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // showUser cases
      .addCase(showUserThunk.pending, (state, action) => {
        state.userShowError = null;
        state.userId = action.meta.arg.userId;
      })
      .addCase(showUserThunk.fulfilled, state => {
        // No state changes needed on success
      })
      .addCase(showUserThunk.rejected, (state, action) => {
        state.userShowError = storableError(action.payload);
      })
      // queryUserListings cases
      .addCase(queryUserListingsThunk.pending, (state, action) => {
        const userId = action.meta.arg.userId;
        // Empty listings only when user id changes
        state.userListingRefs = userId === state.userId ? state.userListingRefs : [];
        state.queryListingsError = null;
      })
      .addCase(queryUserListingsThunk.fulfilled, (state, action) => {
        state.userListingRefs = action.payload.listingRefs;
        state.ownClientListingRefs = action.payload.ownClientListingRefs || [];
        state.listingClassifications = action.payload.listingClassifications || {};
      })
      .addCase(queryUserListingsThunk.rejected, (state, action) => {
        state.userListingRefs = [];
        state.ownClientListingRefs = [];
        state.listingClassifications = {};
        state.queryListingsError = storableError(action.payload);
      })
      // archiveOwnDraft cases
      .addCase(archiveOwnDraftThunk.pending, (state, action) => {
        state.archiveDraftInProgressId = action.meta.arg.listingId.uuid;
        state.archiveDraftError = null;
      })
      .addCase(archiveOwnDraftThunk.fulfilled, (state, action) => {
        const listingId = action.payload.listingId;
        const currentClassification = state.listingClassifications[listingId] || {};
        state.archiveDraftInProgressId = null;
        state.archiveDraftError = null;
        state.listingClassifications[listingId] = {
          ...currentClassification,
          group: LISTING_GROUP_ARCHIVED,
          status: LISTING_STATUS_MANUALLY_ARCHIVED,
        };
      })
      .addCase(archiveOwnDraftThunk.rejected, (state, action) => {
        state.archiveDraftInProgressId = null;
        state.archiveDraftError = action.meta.condition ? null : storableError(action.payload);
      })
      // queryUserReviews cases
      .addCase(queryUserReviewsThunk.pending, state => {
        state.queryReviewsError = null;
      })
      .addCase(queryUserReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(queryUserReviewsThunk.rejected, (state, action) => {
        state.reviews = [];
        state.queryReviewsError = action.payload;
      });
  },
});

export const { setInitialState, setUserId } = profilePageSlice.actions;
export default profilePageSlice.reducer;

// ================ Load data ================ //

const isCurrentUser = (userId, cu) => userId?.uuid === cu?.id?.uuid;

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);
  const isPreviewForCurrentUser = params.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
    const currentUser = getState()?.user?.currentUser;

    if (isPreviewForCurrentUser) {
      if (isCurrentUser(userId, currentUser) && isUserAuthorized(currentUser)) {
        // Scenario: 'active' user somehow tries to open a link for "variant" profile
        return Promise.all([
          dispatch(showUser(userId, config)),
          dispatch(queryUserListings(userId, config)),
          dispatch(queryUserReviews(userId)),
        ]);
      } else if (isCurrentUser(userId, currentUser)) {
        // Handle a scenario, where user (in pending-approval state)
        // tries to see their own profile page.
        // => just set userId to state
        return dispatch(setUserId(userId));
      } else {
        return Promise.resolve({});
      }
    }

    // Fetch data for plain profile page.
    // Note 1: returns 404s if user is not 'active'.
    // Note 2: In private marketplace mode, this page won't fetch data if the user is unauthorized
    const isAuthorized = currentUser && isUserAuthorized(currentUser);
    const isPrivateMarketplace = config.accessControl.marketplace.private === true;
    const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
    const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
    const viewingOwnProfile = isCurrentUser(userId, currentUser);
    // On a private marketplace, show active (approved) current user's own page
    // even if they don't have viewing rights
    const canFetchOwnProfileOnly =
      isPrivateMarketplace && isAuthorized && hasNoViewingRights && viewingOwnProfile;

    if (!canFetchData) {
      return Promise.resolve();
    } else if (canFetchOwnProfileOnly) {
      return Promise.all([
        dispatch(queryUserListings(userId, config, true)),
        dispatch(setUserId(userId)),
      ]);
    }

    return Promise.all([
      dispatch(showUser(userId, config)),
      dispatch(queryUserListings(userId, config, viewingOwnProfile)),
      dispatch(queryUserReviews(userId)),
    ]);
  });
};
